import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Play, Settings, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import apiService from '../services/apiService';
import Header from '../components/Header';

const Container = styled.div`
  min-height: 100vh;
  background-color: #000;
  color: #fff;
  padding: 80px 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Card = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 600px;
  border: 1px solid #333;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.2rem;
  margin-bottom: 20px;
  color: #e50914;
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #252525;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 12px;
`;

const ActionInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ActionName = styled.span`
  font-weight: 600;
  font-size: 1rem;
`;

const ActionDesc = styled.span`
  font-size: 0.85rem;
  color: #a3a3a3;
`;

const RunBtn = styled.button`
  background: ${props => props.disabled ? '#444' : '#e50914'};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.disabled ? '#444' : '#f40612'};
  }
`;

const StatusBox = styled.div`
  margin-top: 10px;
  padding: 10px;
  background: #000;
  border-radius: 6px;
  font-family: monospace;
  font-size: 0.9rem;
  color: ${props => props.error ? '#ff4d4d' : '#4dff88'};
  display: ${props => props.visible ? 'block' : 'none'};
`;

const AdminPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);
    const [lastResult, setLastResult] = useState(null);
    const [isPolling, setIsPolling] = useState(false);

    // Initial Status Check
    React.useEffect(() => {
        checkStatus();
    }, []);

    // Polling Logic
    React.useEffect(() => {
        let interval;
        if (isPolling) {
            interval = setInterval(checkStatus, 2000);
        }
        return () => clearInterval(interval);
    }, [isPolling]);

    const checkStatus = async () => {
        try {
            const res = await apiService.getBatchStatus();
            // Update Last Result if exists
            if (res.lastResult) setLastResult(res.lastResult);

            // Update Current Status
            if (res.status === 'running') {
                setStatus({ msg: `🔄 ${res.message} (${res.progress}%)`, error: false });
                setLoading(true);
                setIsPolling(true);
            } else if (res.status === 'idle' && isPolling) {
                // Just finished
                setIsPolling(false);
                setLoading(false);
                setStatus({ msg: `✅ Finished: ${res.message}`, error: false });
                if (res.lastResult) setLastResult(res.lastResult); // Ensure fresh result
            }
        } catch (e) {
            console.error("Status check failed", e);
        }
    };

    const handleRunNewsBatch = async (testMode = false) => {
        const confirmMsg = testMode
            ? "테스트 모드: 1개의 뉴스만 수집하고 분석합니다. (빠른 실행)\n진행하시겠습니까?"
            : "모든 RSS 피드에서 뉴스 크롤링을 시작하시겠습니까? (시간이 다소 소요됩니다)";

        if (!window.confirm(confirmMsg)) return;

        setLoading(true);
        setIsPolling(true); // Start polling immediately
        setStatus({ msg: testMode ? "🚀 테스트 실행 중 (1 Item)..." : "🚀 전체 배치를 요청 중...", error: false });

        try {
            // Async call - backend handles actual processing
            await apiService.runNewsBatch(testMode);
            checkStatus();
        } catch (e) {
            setStatus({ msg: `❌ 에러 발생: ${e.message}`, error: true });
            setIsPolling(false);
            setLoading(false);
        }
    };

    const handleDeleteNews = async () => {
        if (!window.confirm("모든 뉴스 데이터를 삭제하시겠습니까? (재수집을 위해 필요할 수 있습니다)")) return;

        setLoading(true);
        try {
            const res = await apiService.deleteNews();
            if (res.success) {
                setStatus({ msg: `🗑️ ${res.message}`, error: false });
            } else {
                setStatus({ msg: `❌ 삭제 실패`, error: true });
            }
        } catch (e) {
            setStatus({ msg: `❌ 에러 발생: ${e.message}`, error: true });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Header />
            <Container>
                <h1>System Admin</h1>
                <p style={{ color: '#888', marginBottom: 30 }}>시스템 작업 및 배치 관리</p>

                <Card>
                    <SectionTitle>
                        <Settings size={20} />
                        배치 작업 (Batch Jobs)
                    </SectionTitle>

                    <ActionRow>
                        <ActionInfo>
                            <ActionName>News Crawler (Hourly)</ActionName>
                            <ActionDesc>CNN, Bloomberg, Economist RSS 크롤링 및 AI 요약</ActionDesc>
                        </ActionInfo>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <RunBtn onClick={() => handleRunNewsBatch(true)} disabled={loading} style={{ background: '#333', border: '1px solid #e50914' }}>
                                {loading ? 'Running...' : <><Play size={16} /> Test (1 Item)</>}
                            </RunBtn>
                            <RunBtn onClick={() => handleRunNewsBatch(false)} disabled={loading}>
                                {loading ? 'Running...' : <><Play size={16} /> Run All</>}
                            </RunBtn>
                        </div>
                    </ActionRow>
                    <ActionRow>
                        <ActionInfo>
                            <ActionName>View Crawled News</ActionName>
                            <ActionDesc>수집된 뉴스 피드(최근 50개) 확인하기</ActionDesc>
                        </ActionInfo>
                        <RunBtn onClick={() => navigate('/admin/batch-results')} style={{ background: '#333', border: '1px solid #555' }}>
                            <Settings size={16} /> View Dashboard
                        </RunBtn>
                    </ActionRow>

                    <ActionRow>
                        <ActionInfo>
                            <ActionName>Reset News Data</ActionName>
                            <ActionDesc>기존에 수집된 뉴스 모두 삭제 (오류 수정 후 재수집 시 사용)</ActionDesc>
                        </ActionInfo>
                        <RunBtn onClick={handleDeleteNews} style={{ background: '#333', border: '1px solid #ff4444', color: '#ff6666' }}>
                            <Trash2 size={16} /> Reset
                        </RunBtn>
                    </ActionRow>

                    <StatusBox visible={!!status} error={status?.error}>
                        {status?.msg}
                    </StatusBox>
                </Card>

                {/* Last Execution Result Section */}
                <Card>
                    <SectionTitle>
                        <CheckCircle size={20} />
                        Last Execution Result
                    </SectionTitle>
                    {lastResult ? (
                        <div style={{ padding: 10, background: '#222', borderRadius: 8 }}>
                            <div style={{ color: '#888', fontSize: '0.9rem' }}>Execution Time: {new Date(lastResult.timestamp).toLocaleString()}</div>
                            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginTop: 8 }}>
                                {lastResult.success ? `✅ Success (+${lastResult.added} news)` : '❌ Failed'}
                            </div>
                        </div>
                    ) : (
                        <div style={{ color: '#666' }}>No execution record found.</div>
                    )}
                </Card>

            </Container>
        </>
    );
};

export default AdminPage;
